import React, { useEffect, useState } from "react";
import {
  X,
  Plus,
  Minus,
  Wallet,
  History,
  ChevronLeft,
  ChevronRight,
  User,
  Mail,
  Phone,
  CreditCard,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  XCircle,
  RotateCcw,
} from "lucide-react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const WalletModal = ({ open, onClose, user, refreshRequests }) => {
  const [activeTab, setActiveTab] = useState("form");
  const url = import.meta.env.VITE_URL;

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    amount: "",
    remark: "",
    type: "",
  });

  const [walletHistory, setWalletHistory] = useState([]);
  const [summary, setSummary] = useState({
    totalAdd: 0,
    totalDeduct: 0,
    currentBalance: 0,
  });
  const [loading, setLoading] = useState(false);
  const [walletActive, setWalletActive] = useState(false);
  const [userRequest, setUserRequest] = useState(null);
  const [walletStatus, setWalletStatus] = useState(""); // not_active, active, deactivated

  // 🔹 Check wallet status
  const checkWalletStatus = async () => {
    if (!user?._id) return false;

    try {
      const statusRes = await axios.get(
        `${url}/api/wallet/wallet-status/${user._id}`,
        {
          withCredentials: true,
        }
      );

      if (statusRes.data.success) {
        const { isActive, latestRequest } = statusRes.data.data;
        setWalletActive(isActive);

        if (isActive) {
          setWalletStatus("active");
        } else if (latestRequest?.status === "deactivated") {
          setWalletStatus("deactivated");
        } else {
          setWalletStatus("not_active");
        }

        return isActive;
      }
      return false;
    } catch (error) {
      console.error("Check wallet status error:", error);
      return false;
    }
  };

  // 🔹 Fetch wallet data
  const fetchWallet = async () => {
    if (!user?._id) return;

    try {
      setLoading(true);

      // 1. Check wallet status
      const isActive = await checkWalletStatus();

      // 2. Fetch wallet history if active
      if (isActive) {
        try {
          const historyRes = await axios.get(
            `${url}/api/wallet/user/${user._id}`,
            {
              withCredentials: true,
            }
          );
          if (historyRes.data.success) {
            setWalletHistory(historyRes.data.data || []);
            setSummary(historyRes.data.summary || {});
          }
        } catch (historyErr) {
          console.log("History not available");
        }
      } else {
        setWalletHistory([]);
        setSummary({ totalAdd: 0, totalDeduct: 0, currentBalance: 0 });
      }

      // 3. Check for pending requests
      await fetchUserRequest();
    } catch (err) {
      console.error("Wallet fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Fetch user's pending request
  const fetchUserRequest = async () => {
    if (!user?._id) return;

    try {
      const res = await axios.get(`${url}/api/wallet/pending-requests`, {
        withCredentials: true,
      });

      if (res.data.success) {
        const request = res.data.data.find(
          (req) => req.userId?._id === user._id && req.status === "pending"
        );
        setUserRequest(request);
      }
    } catch (err) {
      console.error("Fetch request error:", err);
    }
  };

  useEffect(() => {
    if (open && user?._id) {
      fetchWallet();
      setCurrentPage(1);
    }
  }, [open, user?._id]);

  // 🔹 Submit add / deduct
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.amount || !formData.type) {
      toast.error("Please enter amount and select type");
      return;
    }

    try {
      const res = await axios.post(
        `${url}/api/wallet/create/${user._id}`,
        formData,
        { withCredentials: true }
      );

      if (res.data.success) {
        toast.success(res.data.message || "Transaction successful");
        setFormData({ amount: "", remark: "", type: "" });
        setActiveTab("history");
        fetchWallet();
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Transaction failed");
    }
  };

  // 🔹 DIRECT ACTIVATE (without request)
  const handleDirectActivate = async () => {
    try {
      const res = await axios.post(
        `${url}/api/wallet/activate/${user._id}`,
        {
          initialAmount: formData.amount || 0,
          remark: formData.remark || "Direct activation by admin",
        },
        { withCredentials: true }
      );

      if (res.data.success) {
        toast.success("Wallet activated successfully!");
        setWalletActive(true);
        setWalletStatus("active");
        setFormData({ amount: "", remark: "", type: "" });
        fetchWallet();
        if (refreshRequests) refreshRequests();
      }
    } catch (err) {
      console.error("Direct activation error:", err);

      // If wallet already exists
      if (err.response?.status === 400) {
        toast.info("Wallet is already active");
        setWalletActive(true);
        setWalletStatus("active");
        fetchWallet();
      } else {
        toast.error(
          err?.response?.data?.message || "Failed to activate wallet"
        );
      }
    }
  };

  // 🔹 DEACTIVATE WALLET
  const handleDeactivateWallet = async () => {
    if (
      !confirm(
        "Are you sure you want to deactivate this wallet? Funds will be preserved."
      )
    ) {
      return;
    }

    try {
      const res = await axios.post(
        `${url}/api/wallet/deactivate/${user._id}`,
        { reason: "Wallet deactivated by admin" },
        { withCredentials: true }
      );

      if (res.data.success) {
        toast.success("Wallet deactivated successfully!");
        setWalletActive(false);
        setWalletStatus("deactivated");
        fetchWallet();
        if (refreshRequests) refreshRequests();
      }
    } catch (err) {
      console.error("Deactivate error:", err);
      toast.error(
        err?.response?.data?.message || "Failed to deactivate wallet"
      );
    }
  };

  // 🔹 APPROVE PENDING REQUEST
  const handleApproveRequest = async () => {
    if (!userRequest) return;

    try {
      if (
        userRequest.requestType === "activation" ||
        userRequest.requestType === "reactivation"
      ) {
        await handleDirectActivate();
      } else if (userRequest.requestType === "fund_add") {
        const res = await axios.post(
          `${url}/api/wallet/approve/${userRequest._id}`,
          { adminNote: "Request approved by admin" },
          { withCredentials: true }
        );

        if (res.data.success) {
          toast.success(`₹${userRequest.requestedAmount} added to wallet`);
          setUserRequest(null);
          fetchWallet();
          if (refreshRequests) refreshRequests();
        }
      }
    } catch (err) {
      console.error("Approve error:", err);
      toast.error(err?.response?.data?.message || "Failed to approve request");
    }
  };

  // 🔹 REJECT PENDING REQUEST
  const handleRejectRequest = async () => {
    if (!userRequest) return;

    try {
      const res = await axios.put(
        `${url}/api/wallet/cancel/${userRequest._id}`,
        { reason: "Request rejected by admin" },
        { withCredentials: true }
      );

      if (res.data.success) {
        toast.success("Request rejected");
        setUserRequest(null);
        fetchUserRequest();
        if (refreshRequests) refreshRequests();
      }
    } catch (err) {
      console.error("Reject error:", err);
      toast.error("Failed to reject request");
    }
  };

  // 🔹 DIRECT FUND ADD (without request)
  const handleDirectFundAdd = async () => {
    if (!formData.amount) {
      toast.error("Please enter amount");
      return;
    }

    try {
      const res = await axios.post(
        `${url}/api/wallet/direct-add/${user._id}`,
        {
          amount: formData.amount,
          remark: formData.remark || "Direct fund add by admin",
        },
        { withCredentials: true }
      );

      if (res.data.success) {
        toast.success(`₹${formData.amount} added to wallet`);
        setFormData({ amount: "", remark: "", type: "" });
        fetchWallet();
      }
    } catch (err) {
      console.error("Direct fund add error:", err);
      toast.error(err?.response?.data?.message || "Failed to add funds");
    }
  };

  // 🔹 Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = walletHistory.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(walletHistory.length / itemsPerPage);

  // 🔹 Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPageButtons = 5;

    if (totalPages <= maxPageButtons) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      pageNumbers.push(1);

      if (startPage > 2) {
        pageNumbers.push("...");
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }

      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-4xl bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="relative px-6 py-5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-full backdrop-blur-sm">
                <Wallet className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Wallet Management</h2>
                <p className="text-emerald-100 text-sm">
                  Managing wallet for{" "}
                  <span className="font-semibold">
                    {user?.username || "User"}
                  </span>
                  <span className="ml-2 px-2 py-0.5 bg-white/30 rounded text-xs">
                    Status: {walletStatus.toUpperCase()}
                  </span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  fetchWallet();
                  fetchUserRequest();
                }}
                className="p-2 hover:bg-white/20 rounded-full transition-all duration-200"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-full transition-all duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <User className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">User</p>
                <p className="font-semibold text-gray-900">
                  {user?.username || "N/A"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-semibold text-gray-900 truncate">
                  {user?.email || "N/A"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Phone className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-semibold text-gray-900">
                  {user?.phone || "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Request Banner */}
        {userRequest && (
          <div className="px-6 py-4 bg-gradient-to-r from-yellow-50 to-amber-50 border-y border-yellow-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-yellow-800">
                    Pending{" "}
                    {userRequest.requestType === "activation"
                      ? "Activation"
                      : userRequest.requestType === "reactivation"
                      ? "Reactivation"
                      : "Fund"}{" "}
                    Request
                  </h3>
                  <p className="text-sm text-yellow-700">
                    {userRequest.requestType === "fund_add"
                      ? `Requesting ₹${userRequest.requestedAmount} to be added`
                      : userRequest.requestType === "reactivation"
                      ? "Requesting wallet reactivation"
                      : "Requesting wallet activation"}
                  </p>
                  {userRequest.userNote && (
                    <p className="text-xs text-yellow-600 mt-1">
                      Note: {userRequest.userNote}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleRejectRequest}
                  className="px-4 py-2 bg-red-100 text-red-700 border border-red-200 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  Reject
                </button>
                <button
                  onClick={handleApproveRequest}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Approve
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Deactivated Wallet Banner */}
        {walletStatus === "deactivated" && (
          <div className="px-6 py-4 bg-gradient-to-r from-red-50 to-pink-50 border-y border-red-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <RotateCcw className="w-6 h-6 text-red-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-red-800">
                    Wallet Deactivated
                  </h3>
                  <p className="text-sm text-red-700">
                    This wallet is currently deactivated. User can request
                    reactivation.
                  </p>
                </div>
              </div>
              <button
                onClick={handleDirectActivate}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Reactivate Now
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          <button
            onClick={() => setActiveTab("form")}
            className={`flex-1 py-4 flex items-center justify-center gap-2 font-semibold transition-all ${
              activeTab === "form"
                ? "bg-white text-emerald-600 border-b-2 border-emerald-600 shadow-sm"
                : "text-gray-600 hover:text-emerald-600 hover:bg-white/50"
            }`}
          >
            <Plus className="w-4 h-4" />
            Add / Deduct Funds
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`flex-1 py-4 flex items-center justify-center gap-2 font-semibold transition-all ${
              activeTab === "history"
                ? "bg-white text-emerald-600 border-b-2 border-emerald-600 shadow-sm"
                : "text-gray-600 hover:text-emerald-600 hover:bg-white/50"
            }`}
          >
            <History className="w-4 h-4" />
            Transaction History
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : (
            <>
              {/* FORM TAB */}
              {activeTab === "form" && (
                <div className="space-y-6">
                  {/* Status Display */}
                  <div
                    className={`p-4 rounded-xl ${
                      walletStatus === "active"
                        ? "bg-emerald-50 border border-emerald-200"
                        : walletStatus === "deactivated"
                        ? "bg-red-50 border border-red-200"
                        : "bg-yellow-50 border border-yellow-200"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {walletStatus === "active" ? (
                          <CheckCircle className="w-6 h-6 text-emerald-600" />
                        ) : walletStatus === "deactivated" ? (
                          <XCircle className="w-6 h-6 text-red-600" />
                        ) : (
                          <AlertCircle className="w-6 h-6 text-yellow-600" />
                        )}
                        <div>
                          <p className="font-semibold">
                            {walletStatus === "active"
                              ? "Wallet Active"
                              : walletStatus === "deactivated"
                              ? "Wallet Deactivated"
                              : "Wallet Not Active"}
                          </p>
                          <p className="text-sm opacity-75">
                            {walletStatus === "active"
                              ? "Ready for transactions"
                              : walletStatus === "deactivated"
                              ? "User needs to request reactivation"
                              : "Activate wallet to proceed"}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {walletStatus === "active" && (
                          <button
                            onClick={handleDeactivateWallet}
                            className="px-3 py-1.5 bg-red-100 text-red-700 border border-red-200 rounded-lg hover:bg-red-200 transition-colors text-sm"
                          >
                            Deactivate
                          </button>
                        )}
                        {walletStatus !== "active" && (
                          <button
                            onClick={handleDirectActivate}
                            className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm"
                          >
                            {walletStatus === "deactivated"
                              ? "Reactivate"
                              : "Activate"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {walletStatus === "active" ? (
                    <>
                      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5 mb-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-emerald-800">
                              Current Balance
                            </p>
                            <p className="text-3xl font-bold text-emerald-900 mt-1">
                              ₹{" "}
                              {summary.currentBalance?.toLocaleString() || "0"}
                            </p>
                          </div>
                          <div className="p-3 bg-emerald-500/20 rounded-full">
                            <Wallet className="w-6 h-6 text-emerald-700" />
                          </div>
                        </div>
                      </div>

                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Transaction Type
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                              <button
                                type="button"
                                onClick={() =>
                                  setFormData({ ...formData, type: "ADD" })
                                }
                                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center justify-center ${
                                  formData.type === "ADD"
                                    ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                    : "border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50"
                                }`}
                              >
                                <Plus className="w-6 h-6 mb-2" />
                                <span className="font-semibold">Add Funds</span>
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setFormData({ ...formData, type: "DEDUCT" })
                                }
                                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center justify-center ${
                                  formData.type === "DEDUCT"
                                    ? "border-red-500 bg-red-50 text-red-700"
                                    : "border-gray-200 hover:border-red-300 hover:bg-red-50/50"
                                }`}
                              >
                                <Minus className="w-6 h-6 mb-2" />
                                <span className="font-semibold">
                                  Deduct Funds
                                </span>
                              </button>
                            </div>
                          </div>

                          <div className="space-y-6">
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Amount (₹)
                              </label>
                              <input
                                type="number"
                                value={formData.amount}
                                required
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    amount: e.target.value,
                                  })
                                }
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                                placeholder="Enter amount"
                                min="0"
                                step="0.01"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Remarks (Optional)
                              </label>
                              <input
                                type="text"
                                value={formData.remark}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    remark: e.target.value,
                                  })
                                }
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                                placeholder="Add a note for this transaction"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-3 pt-4">
                          <button
                            type="button"
                            onClick={handleDirectFundAdd}
                            disabled={!formData.amount}
                            className={`flex-1 py-3.5 bg-gradient-to-r from-purple-600 to-violet-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2`}
                          >
                            <CreditCard className="w-4 h-4" />
                            Quick Add Funds
                          </button>

                          <button
                            type="submit"
                            disabled={!formData.type || !formData.amount}
                            className={`flex-1 py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-xl hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl`}
                          >
                            {formData.type === "ADD"
                              ? "Add Funds"
                              : formData.type === "DEDUCT"
                              ? "Deduct Funds"
                              : "Process Transaction"}
                          </button>
                        </div>
                      </form>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      {walletStatus === "deactivated" ? (
                        <RotateCcw className="w-16 h-16 text-red-400 mx-auto mb-4" />
                      ) : (
                        <AlertCircle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                      )}
                      <h3 className="text-xl font-semibold text-gray-700 mb-2">
                        {walletStatus === "deactivated"
                          ? "Wallet Deactivated"
                          : "Wallet Not Activated"}
                      </h3>
                      <p className="text-gray-500 mb-6">
                        {walletStatus === "deactivated"
                          ? "This user's wallet is deactivated. User needs to request reactivation or you can reactivate it directly."
                          : "This user's wallet is not activated yet."}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                          onClick={handleDirectActivate}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                        >
                          <CheckCircle className="w-5 h-5" />
                          {walletStatus === "deactivated"
                            ? "Reactivate Now"
                            : "Activate Now"}
                        </button>
                        {walletStatus === "deactivated" && (
                          <button
                            onClick={() => setActiveTab("history")}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <History className="w-5 h-5" />
                            View Previous History
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* HISTORY TAB */}
              {activeTab === "history" && (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-2xl p-5 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-emerald-800">
                            Total Added
                          </p>
                          <p className="text-2xl font-bold text-emerald-900 mt-1">
                            ₹ {summary.totalAdd?.toLocaleString() || "0"}
                          </p>
                        </div>
                        <div className="p-3 bg-emerald-500/20 rounded-full">
                          <Plus className="w-5 h-5 text-emerald-700" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-2xl p-5 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-red-800">
                            Total Deducted
                          </p>
                          <p className="text-2xl font-bold text-red-900 mt-1">
                            ₹ {summary.totalDeduct?.toLocaleString() || "0"}
                          </p>
                        </div>
                        <div className="p-3 bg-red-500/20 rounded-full">
                          <Minus className="w-5 h-5 text-red-700" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-5 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-800">
                            Current Balance
                          </p>
                          <p className="text-2xl font-bold text-blue-900 mt-1">
                            ₹ {summary.currentBalance?.toLocaleString() || "0"}
                          </p>
                        </div>
                        <div className="p-3 bg-blue-500/20 rounded-full">
                          <Wallet className="w-5 h-5 text-blue-700" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Type
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Amount
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Remarks
                            </th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Date & Time
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {currentItems.length === 0 ? (
                            <tr>
                              <td
                                colSpan="4"
                                className="px-6 py-12 text-center"
                              >
                                <div className="flex flex-col items-center justify-center text-gray-500">
                                  <History className="w-12 h-12 mb-3 opacity-50" />
                                  <p className="text-lg font-medium">
                                    No transactions yet
                                  </p>
                                  <p className="text-sm">
                                    {walletStatus === "active"
                                      ? "Start by adding funds to the wallet"
                                      : "Wallet is not active"}
                                  </p>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            currentItems.map((w, i) => {
                              const date = new Date(w.createdAt);
                              const formattedDate =
                                date.toLocaleDateString("en-IN");
                              const formattedTime = date.toLocaleTimeString(
                                "en-IN",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              );

                              return (
                                <tr
                                  key={i}
                                  className="hover:bg-gray-50 transition-colors"
                                >
                                  <td className="px-6 py-4">
                                    <span
                                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${
                                        w.type === "ADD"
                                          ? "bg-emerald-100 text-emerald-800"
                                          : "bg-red-100 text-red-800"
                                      }`}
                                    >
                                      {w.type === "ADD" ? (
                                        <Plus className="w-3 h-3 mr-1" />
                                      ) : (
                                        <Minus className="w-3 h-3 mr-1" />
                                      )}
                                      {w.type}
                                    </span>
                                  </td>

                                  <td
                                    className={`px-6 py-4 font-semibold ${
                                      w.type === "ADD"
                                        ? "text-emerald-700"
                                        : "text-red-700"
                                    }`}
                                  >
                                    <div className="flex items-center">
                                      {w.type === "ADD" ? (
                                        <Plus className="w-4 h-4 mr-2 text-emerald-500" />
                                      ) : (
                                        <Minus className="w-4 h-4 mr-2 text-red-500" />
                                      )}
                                      ₹ {w.amount?.toLocaleString()}
                                    </div>
                                  </td>

                                  <td className="px-6 py-4 text-gray-700">
                                    <div
                                      className="max-w-xs truncate"
                                      title={w.remark || "-"}
                                    >
                                      {w.remark || "-"}
                                    </div>
                                  </td>

                                  <td className="px-6 py-4 text-gray-600">
                                    <div className="flex flex-col">
                                      <span className="font-medium">
                                        {formattedDate}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {formattedTime}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {walletHistory.length > 0 && (
                      <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <div className="text-sm text-gray-700">
                          Showing{" "}
                          <span className="font-semibold">
                            {indexOfFirstItem + 1}
                          </span>{" "}
                          to{" "}
                          <span className="font-semibold">
                            {Math.min(indexOfLastItem, walletHistory.length)}
                          </span>{" "}
                          of{" "}
                          <span className="font-semibold">
                            {walletHistory.length}
                          </span>{" "}
                          transactions
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>

                          {getPageNumbers().map((page, index) => (
                            <React.Fragment key={index}>
                              {page === "..." ? (
                                <span className="px-3 py-1 text-gray-500">
                                  ...
                                </span>
                              ) : (
                                <button
                                  onClick={() => handlePageChange(page)}
                                  className={`w-10 h-10 flex items-center justify-center rounded-lg font-medium transition-all ${
                                    currentPage === page
                                      ? "bg-emerald-600 text-white shadow-sm"
                                      : "border border-gray-300 hover:bg-gray-100 text-gray-700"
                                  }`}
                                >
                                  {page}
                                </button>
                              )}
                            </React.Fragment>
                          ))}

                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <ToastContainer
        position="top-center"
        autoClose={3000}
        theme="colored"
        toastClassName="rounded-xl"
      />
    </div>
  );
};

export default WalletModal;
